import React from "react";

import type { IconDefinition } from "@fortawesome/fontawesome-common-types";

import { FontAwesomeIcon } from "@performyard/fontawesome/react-fontawesome";
import { Icon } from "@performyard/py-components";

type SuggestedAction = {
	label: string;
	icon?: IconDefinition;
	onClick: () => void;
	disabled?: boolean;
};

type SuggestedActionsProps = {
	actions: SuggestedAction[];
	/** Text button size. "small" (default) = caption/9px icon; "large" = paragraph-3 (14px)/16px icon. */
	size?: "small" | "large";
	className?: string;
};

export const SuggestedActions: React.FC<SuggestedActionsProps> = ({ actions, size = "small", className }) => {
	if (actions.length === 0) return null;
	// Large follows the panel design (Paragraph 3/Regular, 16px icon); small keeps the legacy popup look.
	const isLarge = size === "large";
	const sizeClasses = isLarge ? "text-paragraph-3 font-normal" : "text-caption font-semibold";
	const iconFontSize = isLarge ? "16px" : "9px";
	// Panel rows are full-width and 32px tall (8px above and below a 16px line), separated by a
	// hairline rather than a gap — node 19647:34474. The popup keeps its compact inline buttons.
	const rowClasses = isLarge ? "w-full justify-start rounded-sm px-0 py-2 text-left" : "p-0";
	return (
		<div className={`flex flex-col ${isLarge ? "items-stretch" : "items-start gap-1"} ${className ?? ""}`}>
			{actions.map((action, index) => (
				<React.Fragment key={action.label}>
					{isLarge && index > 0 && <div className="h-px w-full bg-neutral-2" />}
					<button
						type="button"
						onClick={action.onClick}
						disabled={action.disabled}
						className={`${sizeClasses} ${rowClasses} inline-flex items-center gap-1 border-none bg-transparent ${
							action.disabled
								? "cursor-not-allowed text-neutral-5"
								: "text-primary-5 hover:cursor-pointer hover:text-primary-7 hover:underline active:text-primary-8 active:underline"
						}`}
					>
						{action.icon &&
							(isLarge ? (
								<Icon icon={action.icon} color={null} size={16} />
							) : (
								<FontAwesomeIcon icon={action.icon} style={{ fontSize: iconFontSize }} />
							))}
						<span className="whitespace-nowrap">{action.label}</span>
					</button>
				</React.Fragment>
			))}
		</div>
	);
};
